import { Article, Category } from '@wix/answers-api';
import { Box, Divider, Heading } from '@wix/design-system';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BI, PAGES } from '../../constants';
import { Context } from '../../context';
import { pageClick } from '@wix/bi-logger-new-help-center/v2';
import { useBI } from '../../hooks/useBI';
import { getCategoriesBreadcrumb } from '../../utils/categories';
import { Breadcrumbs } from '../BreadcrumbsV2';
import { pushUrl } from '../InnerLink';
import { NavCategoryTree, NavigationTopics } from '../NavCategoryTree';
import css from './index.module.scss';
import { SubcategoryCard } from './SubcategoryCard/Index';
import { pageClickParams } from '@wix/bi-logger-new-help-center/v2/types';
import { mapPageTypeToSourceName } from '../../utils/bi';
import { ArticleList } from './ArticleList';

export type CategoryPageWrapperProps = {
  currentCategory: Category;
  categoriesArticles: { [key: string]: Article[] };
  categories: Category[];
};

export type LeftNavClickEventInfo = Pick<
  pageClickParams,
  | 'clicked_item_type'
  | 'clicked_url'
  | 'clicked_text'
  | 'clicked_item_order'
  | 'clicked_item_id'
  | 'category_order'
>;

export const CategoryPageWrapper: FunctionComponent<
  CategoryPageWrapperProps
> = ({ currentCategory, categoriesArticles, categories }) => {
  const { locale } = useRouter();
  const { sendBIEvent } = useBI();
  const { isProductionDomain, pageTemplateData, setPageTemplateData } =
    useContext(Context);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] =
    useState<Category>(currentCategory);
  const [categoryArticlesMap, setCategoryArticlesMap] = useState<{
    [key: string]: Article[];
  }>(categoriesArticles);

  const httpClient = useHttpClient();

  const updateCategoryUrl = (url: string) =>
    pushUrl(url, isProductionDomain, true);

  const navigationMenuClickBI = async (itemClicked: LeftNavClickEventInfo) => {
    await sendBIEvent(
      pageClick({
        source_name: BI.SOURCE_NAMES.CATEGORY,
        kb_lang: locale as string,
        item_id: selectedCategory.id,
        ...itemClicked,
      })
    );
  };
  const onCategoryChange = async (
    newCategory: Category,
    event: LeftNavClickEventInfo
  ) => {
    hideCategoryTree();
    setPageTemplateData({
      ...pageTemplateData,
      itemId: newCategory.id,
      pageData: { category: newCategory },
    });
    setSelectedCategory(newCategory);

    if (newCategory.children && newCategory.children.length > 0) {
      await fetchArticles(newCategory.children.map((child) => child.id));
    }

    await updateCategoryUrl(`/${locale}${newCategory.uri}`);
    await navigationMenuClickBI(event);
  };

  const onArticleClick = async (
    article: Article,
    event: LeftNavClickEventInfo
  ) => {
    hideCategoryTree();
    await navigationMenuClickBI(event);
  };

  const onNodeToggle = async (
    category: Category,
    event: LeftNavClickEventInfo
  ) => {
    await navigationMenuClickBI(event);
  };

  useEffect(
    () => () => {
      document.body.classList.remove('modal-open');
    },
    []
  );

  const getCategoryArticles = useCallback(
    async (categoryIds: string[]) => {
      const { data } = await httpClient.get<{ [key: string]: Article[] }>(
        `/api/category/articles`,
        {
          params: {
            locale,
            summarize: true,
            categoryIds,
          },
        }
      );
      return data;
    },
    [httpClient, locale]
  );
  const shouldRenderNavCategoryTree =
    selectedCategory && categories && categories.length > 0;
  const subcategories = selectedCategory?.children;

  const fetchArticles = async (categoryIds: string[]) => {
    if (categoryIds.every((categoryId) => categoryArticlesMap[categoryId])) {
      return categoryIds.reduce(
        (acc, categoryId) => ({
          ...acc,
          [categoryId]: categoryArticlesMap[categoryId],
        }),
        {}
      ) as { [key: string]: Article[] };
    }
    const articles = await getCategoryArticles(
      categoryIds.filter((categoryId) => !categoryArticlesMap[categoryId])
    );
    setCategoryArticlesMap((prev) => ({ ...prev, ...articles }));
    return categoryIds.reduce(
      (acc, categoryId) => ({ ...acc, [categoryId]: articles[categoryId] }),
      {}
    ) as { [key: string]: Article[] };
  };

  const topicsWithArticles = (
    categoriesToTransform: Category[]
  ): NavigationTopics[] =>
    categoriesToTransform.map((categoryToTransform) => ({
      ...categoryToTransform,
      articles: async () =>
        (await fetchArticles([categoryToTransform.id]))[categoryToTransform.id],
      children:
        categoryToTransform.children &&
        topicsWithArticles(categoryToTransform.children),
    }));

  const showCategoryTree = () => {
    if (!categoryMenuOpen) {
      setCategoryMenuOpen(true);
      document.body.classList.add('modal-open');
    }
  };

  const hideCategoryTree = () => {
    if (categoryMenuOpen) {
      setCategoryMenuOpen(false);
      document.body.classList.remove('modal-open');
    }
  };

  const onLinkClick = useCallback(
    async (
      clickedText: string,
      clickedURL: string,
      clickedItemId?: string,
      clickedItemOrder?: number
    ): Promise<void> =>
      sendBIEvent(
        pageClick({
          source_name: mapPageTypeToSourceName(PAGES.CATEGORY),
          kb_lang: locale as string,
          item_id: selectedCategory.id,
          clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
          clicked_url: clickedURL,
          clicked_text: clickedText,
          clicked_item_id: clickedItemId,
          clicked_item_order: clickedItemOrder?.toString(),
        })
      ),
    [selectedCategory.id]
  );

  const onHomePageClick = useCallback(
    async (text: string) => {
      await sendBIEvent(
        pageClick({
          kb_lang: locale as string,
          source_name: mapPageTypeToSourceName(PAGES.CATEGORY),
          item_id: selectedCategory.id,
          clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
          clicked_url: `${location.origin}/${locale}`,
          clicked_text: text,
        })
      );
    },
    [selectedCategory.id]
  );

  const breadcrumbItems = useMemo(
    () => getCategoriesBreadcrumb(categories, selectedCategory.id, onLinkClick, true),
    [selectedCategory, categories, onLinkClick]
  );

  return (
    <div className={css.categoryPage}>
      <Breadcrumbs
        breadcrumbItems={breadcrumbItems}
        onCategoryMenuClick={showCategoryTree}
        onHomePageClick={onHomePageClick}
      />
      <Box direction="horizontal">
        {shouldRenderNavCategoryTree && (
          <NavCategoryTree
            onArticleClick={onArticleClick}
            isCategoryMenuOpen={categoryMenuOpen}
            onCategoryChange={onCategoryChange}
            topics={topicsWithArticles(categories)}
            selectedCategoryId={selectedCategory.id}
            onNodeToggle={onNodeToggle}
            onCategoryMenuClose={hideCategoryTree}
          />
        )}
        <Divider className={css.divider} skin="light" direction="vertical" />
        {subcategories && subcategories.length > 0 ? (
          <Box className={css.categoryDetailsWrapper} direction="vertical">
            <Heading appearance="H2" className={css.categoryTitle}>
              {selectedCategory?.name}
            </Heading>
            <div className={css.subcategories}>
              {subcategories.map((subcategory, idx) => (
                <Box
                  key={subcategory.id}
                  className={css.subcategory}
                  direction="vertical"
                >
                  {
                    <SubcategoryCard
                      categoryOrder={idx + 1}
                      onClickSubcategory={onCategoryChange}
                      onClickArticle={onArticleClick}
                      category={subcategory}
                      articles={categoryArticlesMap[subcategory.id]}
                    />
                  }
                </Box>
              ))}
            </div>
          </Box>
        ) : (
          <ArticleList
            category={selectedCategory}
            categoryArticles={categoryArticlesMap[selectedCategory.id]}
          />
        )}
      </Box>
    </div>
  );
};
